/**
 * Messages in a project: a chat between the people working on it, and the notes for its AI agents (vfs-service notes,
 * src/collab/notes.js and src/collab/chat.js; utils/messagesModel.js has the rules).
 *
 * - openMessages({ projectId, view: 'compose' | 'list', recipient?, agent?, place? }): opens it from anywhere (Team ›
 *   Messages, the "In this Project" panel, the header avatars' list, Home's gadgets). `recipient` picks the
 *   conversation ('person:<email>', 'sub:<sub>', 'agent:<id>', 'everyone', 'people', 'agents'); 'compose' puts the
 *   cursor in its input; `place`: `{ path, line }`, "about this file". Nothing opens without a MessagesHost for that
 *   project (UnifiedTopBar mounts one).
 * - MessagesHost ({ projectId, role?, onOpenFile? }): listens and shows MessagesDialog.
 * - MessagesDialog: the conversations (Everyone, one per colleague, AI agents) with their last message, time and unread
 *   count, beside the open one (collab/ChatPane.jsx); on a phone, a sheet with the conversations as a row on top.
 *   Anyone who can open the project chats with the people (a viewer too); notes for the AI agents need an editor.
 *   New messages arrive live: the presence socket says when the notes change (`notesAt`), and it is polled every few
 *   seconds while that socket is down. What is on the screen is read (this browser's marks, and the bell's).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { getAccess, getNotes } from '../../services/CollabService';
import ChatPane from './ChatPane';
import { BottomSheet } from './BottomSheet';
import useProjectPresence from '../../hooks/useProjectPresence';
import useInbox from '../../hooks/useInbox';
import { usePhone } from '../../hooks/useMediaQuery';
import { getOwnPlace } from '../../utils/presenceClient';
import {
  AGENTS, EVERYONE, agentOptions, canMessageAgents, canSendMessages, conversationFor, conversationsOf, dayLabel, membersOf, readMarks,
  senderLabel, timeLabel, writeReadMark, writtenBy,
} from '../../utils/messagesModel';
import './Collab.css';

const EVENT = 'collab:messages';
const POLL_MS = 4000; // no presence socket: every few seconds while open
const LIVE_POLL_MS = 30000; // with it: only to catch a frame that was missed

/** Opens Messages for a project (see the file's comment). */
export function openMessages(detail) {
  if (typeof window === 'undefined' || !detail?.projectId) { return; }
  window.dispatchEvent(new CustomEvent(EVENT, { detail }));
}

/** The project's members and notes while `open`: reloaded when the socket says so (`notesAt`), polled, and on `collab:note`. */
function useMessagesData(projectId, open, { notesAt = null, live = false } = {}) {
  const [access, setAccess] = useState(null);
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(null);
  // Sent from here and not in an answer yet (a load that started before the send): kept for a minute.
  const local = useRef(new Map());
  const load = useCallback(() => getNotes(projectId, { limit: 300 })
    .then((answer) => {
      const list = answer?.notes || [];
      const now = Date.now();
      list.forEach((n) => local.current.delete(n.id));
      local.current.forEach((n, id) => { if (now - n.sentAt > 60000) { local.current.delete(id); } });
      setNotes([...local.current.values()].map((kept) => kept.note).concat(list));
      setError(null);
    })
    .catch((err) => { setError(err.message || 'The messages could not be loaded.'); setNotes((before) => before || []); }), [projectId]);
  useEffect(() => {
    if (!open || !projectId) { return undefined; }
    let cancelled = false;
    setNotes(null);
    getAccess(projectId).then((answer) => { if (!cancelled) { setAccess(answer || {}); } }).catch(() => { if (!cancelled) { setAccess({}); } });
    load();
    const onNote = (event) => { if (!event.detail?.projectId || event.detail.projectId === projectId) { load(); } };
    window.addEventListener('collab:note', onNote);
    return () => { cancelled = true; window.removeEventListener('collab:note', onNote); };
  }, [open, projectId, load]);
  useEffect(() => {
    if (!open || !projectId) { return undefined; }
    const timer = setInterval(() => { if (typeof document === 'undefined' || !document.hidden) { load(); } }, live ? LIVE_POLL_MS : POLL_MS);
    return () => clearInterval(timer);
  }, [open, projectId, live, load]);
  const lastLive = useRef(notesAt);
  useEffect(() => {
    if (open && notesAt && notesAt !== lastLive.current) { load(); }
    lastLive.current = notesAt;
  }, [open, notesAt, load]);
  // A sent message shows at once, before the next load.
  const add = useCallback((note) => {
    local.current.set(note.id, { note, sentAt: Date.now() });
    setNotes((before) => [note, ...(before || []).filter((n) => n.id !== note.id)]);
  }, []);
  return { access, notes, error, reload: load, add };
}

/** The last message of a conversation, in one line: "You: …", "Alice: …". */
function preview(conversation, me) {
  const last = conversation.last;
  if (!last) { return conversation.key === EVERYONE ? 'Everyone in this project' : conversation.key === AGENTS ? 'Notes for the AI agents' : 'No messages yet'; }
  const who = writtenBy(last, me) && last.by?.kind !== 'agent' ? 'You' : conversation.kind === 'person' ? null : senderLabel(last, me).split(' ')[0];
  return `${who ? `${who}: ` : ''}${last.text}`;
}

function whenOf(iso) {
  const day = dayLabel(iso);
  return day === 'Today' ? timeLabel(iso) : day;
}

function ConversationList({ conversations, active, me, onPick }) {
  return (
    <nav aria-label="Conversations" className="collab-chat-list">
      <ul>
        {conversations.map((c) => (
          <li key={c.key}>
            <button
              aria-current={c.key === active ? 'true' : undefined}
              aria-label={`${c.label}${c.unread ? `, ${c.unread} unread` : ''}`}
              className={`collab-chat-conv${c.key === active ? ' is-on' : ''}${c.unread ? ' has-unread' : ''}`}
              data-testid={`conversation-${c.key}`}
              onClick={() => onPick(c.key)}
              type="button"
            >
              <span className="collab-chat-conv-top">
                <strong>{c.label}{c.role === 'viewer' ? <span className="collab-muted"> (viewer)</span> : null}</strong>
                {c.last && <span className="collab-muted collab-small">{whenOf(c.last.at)}</span>}
              </span>
              <span className="collab-chat-conv-bottom">
                <span className="collab-chat-conv-last">{preview(c, me)}</span>
                {c.unread > 0 && <span aria-hidden="true" className="collab-badge" data-testid={`conversation-unread-${c.key}`}>{c.unread}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

ConversationList.propTypes = {
  active: PropTypes.string,
  conversations: PropTypes.array.isRequired,
  me: PropTypes.object,
  onPick: PropTypes.func.isRequired,
};

export function MessagesDialog({ projectId, open, onClose, view = 'list', recipient = null, agent = null, place = null, role = null, onOpenFile = null }) {
  const phone = usePhone();
  const snapshot = useProjectPresence(open ? projectId : null);
  const { access, notes, error, reload, add } = useMessagesData(projectId, open, { notesAt: snapshot?.notesAt || null, live: snapshot?.status === 'open' });
  const { inbox, read: readInbox } = useInbox({ enabled: open });
  const known = useRef(new Map());
  [agent, ...(snapshot?.agents || [])].forEach((a) => { if (a?.id) { known.current.set(a.id, a); } });

  const me = useMemo(() => access?.you || (snapshot?.you ? { sub: snapshot.you.sub || null, email: null } : null), [access, snapshot?.you]);
  const members = useMemo(() => membersOf(access, me), [access, me]);
  const myRole = access?.role ?? role;
  const canPeople = access !== null && canSendMessages(myRole);
  const canAgents = access !== null && canMessageAgents(myRole);
  const agents = useMemo(() => agentOptions({ snapshot, notes: notes || [], me, knownAgents: [...known.current.values()] }), [snapshot, notes, me]);

  const [seen, setSeen] = useState(() => readMarks(projectId));
  const [active, setActive] = useState(null); // { key, agentId? }
  const [since, setSince] = useState({}); // conversation -> its read marks when it was opened (the "New" line)
  const [focus, setFocus] = useState(view === 'compose');
  const conversations = useMemo(
    () => conversationsOf(notes || [], { me, members, seen, withAgents: canAgents || active?.key === AGENTS }),
    [notes, me, members, seen, canAgents, active?.key],
  );
  const pick = useCallback((key, agentId = null) => {
    setSince((all) => (all[key] ? all : { ...all, [key]: readMarks(projectId) }));
    setActive({ key, agentId });
  }, [projectId]);
  // Once the members are known: the conversation the caller asked for (a person presence knows by sub: their address).
  useEffect(() => {
    if (!open || !access || active) { return; }
    const wanted = conversationFor(recipient, members) || { key: EVERYONE };
    pick(wanted.key, wanted.agentId || null);
  }, [open, access, active, recipient, members, pick]);
  const current = conversations.find((c) => c.key === active?.key) || conversations[0];

  // On the screen now: read, here (this browser's marks) and in the bell.
  const inboxUnread = useMemo(() => {
    const ids = new Map();
    (inbox?.items || []).forEach((item) => {
      if (item.kind === 'note' && !item.read && item.agentNote?.id && (!item.projectId || item.projectId === projectId)) { ids.set(item.agentNote.id, item.id); }
    });
    return ids;
  }, [inbox, projectId]);
  const currentKey = current?.key || null;
  const currentLast = current?.last?.at || null;
  const currentIds = current ? current.messages.flatMap((m) => m.ids).join(' ') : '';
  useEffect(() => {
    if (!open || !notes || !currentKey || (typeof document !== 'undefined' && document.hidden)) { return; }
    if (currentLast && writeReadMark(projectId, currentKey, currentLast)) { setSeen(readMarks(projectId)); }
    const ids = currentIds.split(' ').filter((id) => inboxUnread.has(id));
    if (ids.length) { readInbox(ids.map((id) => inboxUnread.get(id))); }
  }, [open, notes, currentKey, currentLast, currentIds, projectId, inboxUnread, readInbox]);

  // Said out loud (screen readers): a new message from someone else, in any conversation.
  const [announce, setAnnounce] = useState('');
  const newest = useRef(null);
  useEffect(() => {
    if (!notes) { return; }
    const latest = notes.reduce((best, n) => (!best || String(n.at) > String(best.at) ? n : best), null);
    if (newest.current && latest && latest.id !== newest.current && !writtenBy(latest, me)) {
      setAnnounce(`New message from ${senderLabel(latest, me)}: ${String(latest.text || '').slice(0, 140)}`);
    }
    newest.current = latest ? latest.id : '';
  }, [notes, me]);

  const openFile = onOpenFile ? (target) => { onClose(); onOpenFile(target); } : null;
  let cannotSend = null;
  if (current?.key === AGENTS && !canAgents) { cannotSend = 'You can view this project: notes for the AI agents come from its editors and owner. You can write to the people here.'; }
  if (current?.gone) { cannotSend = `${current.label} is not in this project any more.`; }
  const canSend = Boolean(current) && !cannotSend && (current.key === AGENTS ? canAgents : canPeople);
  const unreadTotal = conversations.reduce((sum, c) => sum + c.unread, 0);

  const body = (
    <div className={`collab-chat${phone ? ' is-phone' : ''}`} data-testid={view === 'compose' ? 'message-dialog' : 'messages-dialog'}>
      <div aria-live="polite" className="collab-sr-only" data-testid="messages-live">{announce}</div>
      {error && <div className="collab-error collab-small collab-chat-error" role="alert">{error}</div>}
      {notes === null || !current ? <div className="collab-muted collab-chat-loading">Loading…</div> : (
        <>
          <ConversationList active={current.key} conversations={conversations} me={me} onPick={(key) => { setFocus(!phone); pick(key); }} />
          <ChatPane
            agentId={current.key === AGENTS ? active?.agentId || null : null}
            agents={agents}
            autoFocus={focus}
            canSend={canSend}
            cannotSend={cannotSend}
            conversation={current}
            me={me}
            onAgentChange={(agentId) => setActive((before) => ({ ...before, agentId }))}
            onChanged={reload}
            onOpenFile={openFile}
            onSent={add}
            place={place}
            projectId={projectId}
            since={since[current.key] || null}
          />
        </>
      )}
    </div>
  );
  const title = unreadTotal ? `Messages · ${unreadTotal} new` : 'Messages';
  if (phone) {
    return <BottomSheet onClose={onClose} open={open} testId="messages-sheet" title={title}>{body}</BottomSheet>;
  }
  return (
    <Modal className="collab-chat-modal" destroyOnHidden footer={null} onCancel={onClose} open={open} title={title} width={860}>
      {body}
    </Modal>
  );
}

MessagesDialog.propTypes = {
  agent: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onOpenFile: PropTypes.func,
  open: PropTypes.bool,
  place: PropTypes.shape({ path: PropTypes.string, line: PropTypes.number }),
  projectId: PropTypes.string.isRequired,
  recipient: PropTypes.string,
  role: PropTypes.string,
  view: PropTypes.oneOf(['compose', 'list']),
};

/** Shows MessagesDialog when openMessages() is called for `projectId`. */
export default function MessagesHost({ projectId, role = null, onOpenFile = null }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    const onOpen = (event) => {
      const detail = event.detail || {};
      if (detail.projectId !== projectId) { return; }
      // From Code: about the file open now (and the cursor's line), unless the caller said where.
      const place = detail.place !== undefined ? detail.place : (detail.view === 'compose' ? getOwnPlace(projectId) : null);
      setState({ view: detail.view === 'compose' ? 'compose' : 'list', recipient: detail.recipient || null, agent: detail.agent || null, place, key: Date.now() });
    };
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, [projectId]);
  if (!projectId || !state) { return null; }
  return (
    <MessagesDialog
      agent={state.agent}
      key={state.key}
      onClose={() => setState(null)}
      onOpenFile={onOpenFile}
      open
      place={state.place}
      projectId={projectId}
      recipient={state.recipient}
      role={role}
      view={state.view}
    />
  );
}

MessagesHost.propTypes = {
  onOpenFile: PropTypes.func,
  projectId: PropTypes.string,
  role: PropTypes.string,
};
